/**
 * New request's API shapes — `docs/new-request.md` §11.2 and §11.4.
 *
 * One schema per call, shared by the sheet and the server, so a field the
 * sheet sends is a field the server reads.
 */
import { z } from 'zod';
import { regionCode } from '../regions.js';
import { PHONE_KINDS, PREFERS, PROPERTY_TYPE_VALUES } from './types.js';
import { WANTED_KINDS } from './when.js';
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const newContactSchema = z.object({
    firstName: z.string().trim().min(1, 'Add a first name.').max(80),
    lastName: z.string().trim().min(1, 'Add a last name — a contact needs one.').max(80),
    company: z.string().trim().max(160).nullish(),
    title: z.string().trim().max(120).nullish(),
    phone: z.object({
        kind: z.enum(PHONE_KINDS),
        number: z.string().trim().min(7).max(40),
    }).nullish(),
    email: z.string().trim().email('That email doesn’t look right.').max(200).nullish()
        .or(z.literal('').transform(() => null)),
    prefers: z.enum(PREFERS).default('either'),
    /** After *It's someone else*: a shared number is not a duplicate (§6.3). */
    allowPhoneMatch: z.literal(true).optional(),
});
export const newPropertySchema = z.object({
    line1: z.string().trim().min(1, 'Add the address.').max(200),
    line2: z.string().trim().max(120).nullish(),
    city: z.string().trim().min(1).max(120),
    state: regionCode,
    postalCode: z.string().trim().min(3).max(12),
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    placeId: z.string().max(300).nullish(),
    /** Google confirmed it, or a pin was placed by hand (§7.1). */
    verified: z.boolean(),
    type: z.enum(PROPERTY_TYPE_VALUES),
    name: z.string().trim().min(1).max(200),
    /** The name still follows the rules; the server works it out again. */
    nameIsAuto: z.boolean(),
    zoneId: z.string().uuid().nullish(),
    orgUnitId: z.string().uuid(),
});
export const wantedChoiceSchema = z.object({
    kind: z.enum(WANTED_KINDS),
    date: day.nullish(),
    date2: day.nullish(),
    season: z.string().regex(/^(winter|spring|summer|fall)-\d{4}$/).nullish(),
});
/**
 * How the confirmation goes. An email may go to more addresses than the
 * contact's own and say something other than the standard words (Aaron, step
 * 28: "sometimes circumstances will call for customization") — `message`
 * replaces the email's opening paragraph; the signature and the line about
 * changing the time stay. A text is always the standard one.
 */
export const confirmSchema = z.object({
    channel: z.enum(['sms', 'email']),
    alsoTo: z.array(z.string().trim().toLowerCase().email('That isn’t an email address.').max(254))
        .max(5, 'Five more addresses at most.').default([]),
    message: z.string().trim().min(1).max(2000).nullish(),
});
export const newRequestSchema = z.object({
    /** The sheet's id, made when it opens: the idempotency key (§11.1). */
    requestId: z.string().uuid(),
    contact: z.union([z.object({ id: z.string().uuid() }), newContactSchema]),
    /*
     * Create makes what is there (Aaron, step 28): a contact always; a place if
     * one was given; a job only on a place; a site visit only on a job.
     */
    property: z.union([
        z.object({ id: z.string().uuid(), linkContact: z.literal(true).optional() }),
        newPropertySchema,
    ]).nullish(),
    job: z.object({
        title: z.string().trim().min(1, 'Add a title.').max(160),
        divisionCode: z.string().trim().min(1, 'Choose a division.').max(60),
        leadSourceId: z.string().uuid().nullish(),
        /** *Add “Nextdoor” as a lead source* (Board 6 A). */
        newLeadSource: z.string().trim().min(1).max(80).nullish(),
        wanted: wantedChoiceSchema.nullish(),
        budgetLow: z.number().nonnegative().nullish(),
        budgetHigh: z.number().nonnegative().nullish(),
    }).nullish(),
    /** What they want. */
    description: z.string().trim().max(4000).default(''),
    /** Who goes. */
    salespersonId: z.string().uuid().nullish(),
    /** No `startsAt`: not booked yet (Create, book later with the Appointment started). */
    appointment: z.object({
        startsAt: z.string().datetime({ offset: true }).nullish(),
        minutes: z.number().int().min(15).max(480),
    }).nullish(),
    /** Absent: no confirmation. Never with a not-booked appointment. */
    confirm: confirmSchema.nullish(),
    /** *Create, book later*: the answer says where to land (§11.4). */
    bookLater: z.boolean().default(false),
}).superRefine((input, check) => {
    if (input.job && !input.property) {
        check.addIssue({ code: 'custom', path: ['property'], message: 'Add the address.' });
    }
    if (!input.job && (input.appointment || input.salespersonId || input.confirm)) {
        check.addIssue({ code: 'custom', path: ['job'], message: 'A site visit needs a job — add a title and a division.' });
    }
});
export const bookRequestSchema = z.object({
    requestId: z.string().uuid(),
    jobId: z.string().uuid(),
    appointmentId: z.string().uuid().nullish(),
    salespersonId: z.string().uuid(),
    startsAt: z.string().datetime({ offset: true }),
    minutes: z.number().int().min(15).max(480),
    description: z.string().trim().max(4000).default(''),
    confirm: confirmSchema.nullish(),
});
//# sourceMappingURL=api.js.map